import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../config/api';
import TokenManager from '../services/TokenManager';
import { buildPlacePhotoSet } from '../utils/placePhotoSet';
import { viewLabelCaption } from '../utils/viewLabelCaption';

/**
 * usePlacePhotos — the photo set for one place, for surfaces that show more
 * than a cover photo (the planner map card, the profile map's card).
 *
 * Order is deliberate and defers to what the cover cascade already decided
 * (src/utils/placePhotoSet.js owns it, shared with the place page's hero):
 *
 *   1. the cover the caller passes in (own cover > own photos > verified venue
 *      > featured community > ... — server/services/photoSourceRegistry.js)
 *   2. the place's licensed photos — Tiqets' images, then its Commons cover +
 *      gallery (server/services/placePhotoSetService.js)
 *   3. admin-approved public community photos for the same place
 *
 * So adding this hook to a surface never changes which photo shows first — it
 * only appends. Both halves come from /api/places/:placeId/photo-set. Its
 * community rows are /community-photos' rows, which already apply the
 * disclosure rules (admin_approved = 1, visibility = 'public',
 * moderation_status = 'approved') — plus, for a signed-in viewer, their OWN
 * photos at the place in any visibility/approval state (own photos, own eyes;
 * server-side owner arm, 2026-09-03). The licensed half is catalogue imagery,
 * nobody's disclosure. This hook must not grow a path that widens the
 * community set further; anything more (co-traveller photos, private trip
 * pools) is a disclosure decision and belongs behind the rules in
 * docs/PHOTO_PRIVACY_MODEL.md.
 *
 * Fails soft in every direction: a fetch error leaves the caller with exactly
 * the cover it passed in — which is what the surface rendered before this
 * hook existed.
 *
 * Every emitted photo carries an `attribution` ({ text, uri } or null) — see
 * placePhotoSet.js. Rich attribution for the cover (author + licence, which is
 * not derivable from a URL) is passed in via `coverAttribution`.
 */

// Module-level cache: opening the same marker twice in a session shouldn't
// refetch. JSON endpoints deliberately carry no Cache-Control (see the caching
// policy in CLAUDE.md), so caching lives here where mutations can bust it.
const cache = new Map(); // `${viewer}|${placeId}|${limit}|${excludeTripId}` -> { licensed, community, fetchedAt }
const TTL_MS = 5 * 60 * 1000;

/** Bust cached photos after an upload/removal. Omit placeId to clear all. */
export function invalidatePlacePhotos(placeId) {
  if (!placeId) {
    cache.clear();
    return;
  }
  // Entries are keyed by viewer, place, limit and trip filter (below), so a
  // place's entries are every key holding it in the place segment — deleting
  // by the bare placeId matched none of them.
  const segment = `|${placeId}|`;
  for (const key of [...cache.keys()]) {
    if (key.includes(segment)) cache.delete(key);
  }
}

// Synthetic and saved-item IDs aren't real places and have no photos.
function isFetchablePlaceId(placeId) {
  const id = String(placeId || '');
  return !!id && !id.startsWith('saved_') && !id.startsWith('custom_');
}

const EMPTY = { forPlace: null, licensed: [], community: [] };

export default function usePlacePhotos(placeId, {
  cover = null,
  coverAlt = '',
  coverAttribution = null,
  // The cover's intrinsic pixel size, when the caller already knows it (a map
  // marker hands over the dimensions of the thumbnail it has already decoded).
  // Community rows carry width/height from the server; the cover is synthesised
  // here and so had none — and since the cover is photo #0, it is the one a
  // consumer sizes its frame from. Without these that consumer has to guess an
  // aspect and correct it a frame later, which reads as the card jumping.
  coverWidth = null,
  coverHeight = null,
  enabled = true,
  limit = 10,
  excludeTripId = null
} = {}) {
  const { t } = useTranslation();
  // rows + the placeId they belong to, set together: fetched state only
  // updates in the effect (after commit), so on the render where placeId
  // changes the memo below would otherwise emit the NEW cover with the OLD
  // place's rows for one frame.
  const [fetched, setFetched] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  // Guards the async setState against a marker being closed (or swapped for
  // another place) while its request is still in flight.
  const requestedFor = useRef(null);

  const shouldFetch = enabled && isFetchablePlaceId(placeId);

  useEffect(() => {
    if (!shouldFetch) {
      setFetched(EMPTY);
      return;
    }

    // The fetch varies by limit and excludeTripId, so the cache key must too
    // — a placeId-only key would serve one surface's filtered/truncated set
    // to another (e.g. the trip view's excludeTripId result to a surface
    // that wanted the full community set, or vice versa).
    //
    // It ALSO varies by viewer (the community half's owner arm returns the
    // caller's own private/unapproved photos), so the key carries the auth
    // token: on a shared device, user B must never be served user A's
    // cached private photos. invalidateSessionCaches flushes this cache at
    // every auth boundary as the primary guarantee; the token in the key is
    // defense in depth for any path that misses the flush.
    const key = `${TokenManager.getTokenSync() || 'anon'}|${placeId}|${limit}|${excludeTripId || ''}`;
    requestedFor.current = key;

    const cached = cache.get(key);
    if (cached && Date.now() - cached.fetchedAt < TTL_MS) {
      setFetched({ forPlace: placeId, licensed: cached.licensed, community: cached.community });
      return;
    }

    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams({ limit: String(limit) });
    if (excludeTripId) params.set('excludeTripId', String(excludeTripId));

    fetch(`${API_BASE}/api/places/${encodeURIComponent(placeId)}/photo-set?${params}`, {
      headers: TokenManager.getAuthHeaders()
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled || requestedFor.current !== key) return;
        const licensed = data?.success ? (data.licensed || []) : [];
        const community = data?.success ? (data.photos || []) : [];
        cache.set(key, { licensed, community, fetchedAt: Date.now() });
        setFetched({ forPlace: placeId, licensed, community });
      })
      // Nothing about this failing should surface to the user — they keep
      // the cover.
      .catch(() => { if (!cancelled) setFetched({ ...EMPTY, forPlace: placeId }); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [shouldFetch, placeId, limit, excludeTripId]);

  const captionFor = useCallback((row) => viewLabelCaption(row.view_label, t), [t]);

  const photos = useMemo(() => {
    // Only rows fetched FOR this place — stale rows from the previous place
    // (still in state for the first render after a switch) are dropped.
    const current = fetched.forPlace === placeId ? fetched : EMPTY;
    return buildPlacePhotoSet({
      cover,
      coverAlt,
      coverAttribution,
      coverWidth,
      coverHeight,
      licensed: current.licensed,
      community: current.community,
      captionFor
    });
  }, [cover, coverAlt, coverAttribution, coverWidth, coverHeight, fetched, placeId, captionFor]);

  return { photos, loading };
}
